<template>
  <div>
    <div class="alert alert-info">
      <i class="fas fa-info-circle me-2"></i>
      Please ensure your CSV file has the correct columns according to the
      model.
    </div>
    <button
      class="btn btn-sm btn-primary float-right"
      @click="downloadSampleCSV"
    >
      <i class="fas fa-download me-1"></i> Download example CSV
    </button>

    <div class="mb-4">
      <label class="form-label">Select the CSV file</label>
      <div class="row">
        <div class="col-md-8">
          <input
            type="file"
            class="form-control"
            accept=".csv"
            @change="handleFileUpload"
          />
        </div>
        <div class="col-md-4">
          <select
            class="form-select"
            v-model="selectedDelimiter"
            @change="handleFileUpload"
          >
            <option value=",">Separator: Comma (,)</option>
            <option value=";">Separator: Semicolon (;)</option>
            <option value="|">Separator: Pipe (|)</option>
            <option value="\t">Separator: Tabulation</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="previewData.length > 0" class="mb-4">
      <h4>Preview of data</h4>
      <div class="table-responsive">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th v-for="header in previewHeaders" :key="header">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in previewData" :key="index">
              <td v-for="header in previewHeaders" :key="header">
                {{ row[header] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mapeo manual de columnas y campo único -->
    <div v-if="previewHeaders.length > 0" class="mb-4">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fas fa-link me-2"></i>Column Mapping & Duplicate Detection
          </h5>
        </div>
        <div class="card-body">
          <!-- Selector de campo único para duplicados -->
          <div class="mb-4">
            <label class="form-label">
              <i class="fas fa-key me-1"></i>Unique Field for Duplicate Detection
              <small class="text-muted">(Select a field to use for finding and updating existing items)</small>
            </label>
            <select
              class="form-select"
              v-model="uniqueFieldForUpdate"
            >
              <option value="">-- Don't update duplicates, always create new --</option>
              <option value="name">name</option>
              <option value="idModel">Id (idModel)</option>
              <option
                v-for="field in availableFieldsForUnique"
                :key="field.key"
                :value="field.key"
              >
                {{ field.label }} ({{ field.key }})
              </option>
            </select>
            <small class="form-text text-muted">
              If a field is selected, items with the same value in this field will be updated instead of creating duplicates.
            </small>
          </div>

          <!-- Modo de importación (solo visible si hay campo único seleccionado) -->
          <div v-if="uniqueFieldForUpdate" class="mb-4">
            <label class="form-label">
              <i class="fas fa-cog me-1"></i>Import Mode
              <small class="text-muted">(Choose how to handle items that don't exist)</small>
            </label>
            <select
              class="form-select"
              v-model="importMode"
            >
              <option value="update_and_create">Update existing and create new items</option>
              <option value="update_only">Only update existing items (skip new ones)</option>
            </select>
            <small class="form-text text-muted">
              <span v-if="importMode === 'update_and_create'">
                Items that exist will be updated, and new items will be created.
              </span>
              <span v-else>
                Only items that already exist (based on the unique field) will be updated. New items will be skipped.
              </span>
            </small>
          </div>

          <!-- Mapeo manual de columnas -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="form-label mb-0">
                <i class="fas fa-columns me-1"></i>Column Mapping
              </label>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="autoMapColumns"
              >
                <i class="fas fa-magic me-1"></i>Auto Map
              </button>
            </div>
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th style="width: 30%;">CSV Column</th>
                    <th style="width: 70%;">Map to Field</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="header in previewHeaders" :key="header">
                    <td>
                      <strong>{{ header }}</strong>
                    </td>
                    <td>
                      <select
                        class="form-select form-select-sm"
                        v-model="columnMapping[header]"
                      >
                        <option value="">-- Ignore this column --</option>
                        <option value="name">name</option>
                        <option value="idModel">Id (idModel)</option>
                        <optgroup
                          v-for="field in modelFields"
                          :key="field.key"
                          :label="field.label"
                        >
                          <option
                            v-if="field.type !== 'group'"
                            :value="field.key"
                          >
                            {{ field.label }} ({{ field.key }})
                          </option>
                          <template v-if="field.type === 'group'">
                            <option :value="field.key">
                              {{ field.label }} ({{ field.key }}) — grupo completo
                              {{ field.isArray ? "[JSON array]" : "[JSON objeto]" }}
                            </option>
                            <option
                              v-for="subField in field.fields"
                              :key="subField.key"
                              :value="`${field.key}.${subField.key}`"
                            >
                              {{ field.label }} → {{ subField.label }} ({{ field.key }}.{{ subField.key }})
                            </option>
                          </template>
                        </optgroup>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Barra de progreso -->
    <div v-if="isImporting" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span>Import progress</span>
        <span>{{ importProgress }}%</span>
      </div>
      <div class="progress">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          :style="{ width: importProgress + '%' }"
          :aria-valuenow="importProgress"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {{ importProgress }}%
        </div>
      </div>
      <div class="mt-2 text-muted">
        <small>
          Processing row {{ currentRow }} of {{ totalRows }} ({{
            processedRows
          }}
          rows processed, {{ successCount }} successful{{ skippedCount > 0 ? `, ${skippedCount} skipped` : '' }}{{ errorCount > 0 ? `, ${errorCount} with error` : '' }})
        </small>
      </div>
    </div>
    <div v-if="importSummary && !isImporting" class="alert alert-info mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <strong>Import finished</strong>
        <small class="text-muted">
          {{ importSummary.success }} created/updated, {{ importSummary.skipped }} skipped, {{ importSummary.errors }} errors
        </small>
      </div>
      <div
        v-if="importSummary.skippedItems && importSummary.skippedItems.length > 0"
        style="max-height: 220px; overflow-y: auto"
      >
        <div class="fw-semibold mb-1">Skipped items</div>
        <ul class="mb-0 ps-3">
          <li v-for="(skippedItem, idx) in importSummary.skippedItems" :key="idx">
            {{ formatSkippedItem(skippedItem) }}
          </li>
        </ul>
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2">
      <button class="btn btn-secondary" @click="handleCancel">Cancel</button>
      <button
        class="btn btn-primary"
        @click="importData"
        :disabled="!selectedFile || isImporting"
      >
        <span v-if="isImporting">
          <i class="fas fa-spinner fa-spin me-2"></i>
          Importing...
        </span>
        <span v-else>
          <i class="fas fa-file-import me-2"></i>
          Import
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import Papa from "papaparse";
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "ImportComponent",
  props: {
    modelName: {
      type: String,
      required: true,
    },
    modelId: {
      type: String,
      required: true,
    },
    modelFields: {
      type: Array,
      required: true,
    },
    lists: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      selectedFile: null,
      previewData: [],
      previewHeaders: [],
      isImporting: false,
      referenceOptions: {},
      listOptions: {},
      selectedDelimiter: ",",
      // Nuevas propiedades para el progreso
      importProgress: 0,
      currentRow: 0,
      totalRows: 0,
      processedRows: 0,
      successCount: 0,
      errorCount: 0,
      skippedCount: 0,
      // Nuevas propiedades para mapeo manual y detección de duplicados
      columnMapping: {},
      uniqueFieldForUpdate: "",
      importMode: "update_and_create", // "update_and_create" o "update_only"
      cancelRequested: false,
      requestAbortController: null,
      refModelItemsCache: {}, // key: referenced model, value: full items array
      refModelItemsPromises: {}, // key: referenced model, value: in-flight promise
      importSummary: null,
    };
  },
  computed: {
    availableFieldsForUnique() {
      // Campos que pueden usarse para detectar duplicados (excluir grupos y arrays complejos)
      return this.modelFields.filter(
        (field) =>
          field.type !== "group" &&
          (field.type === "string" ||
            field.type === "number" ||
            field.type === "autoincrement" ||
            field.type === "email" ||
            field.key === "name")
      );
    },
  },
  mounted() {
    console.log("Component mounted, lists:", this.lists);
    if (!this.lists || !Array.isArray(this.lists)) {
      console.error("Lists prop is not valid:", this.lists);
      return;
    }
    this.loadAllReferenceOptions();
    this.loadListOptions();
  },
  beforeDestroy() {
    if (this.requestAbortController) {
      this.requestAbortController.abort();
      this.requestAbortController = null;
    }
  },
  methods: {
    handleCancel() {
      if (this.isImporting) {
        this.cancelRequested = true;
        if (this.requestAbortController) {
          this.requestAbortController.abort();
        }
      }
      this.$emit("close");
    },
    isRequestCancelled(error) {
      return (
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        error?.message === "IMPORT_CANCELLED"
      );
    },
    throwIfImportCancelled() {
      if (this.cancelRequested) {
        throw new Error("IMPORT_CANCELLED");
      }
    },
    getRequestConfig() {
      if (this.requestAbortController) {
        return { signal: this.requestAbortController.signal };
      }
      return {};
    },
    formatSkippedItem(skippedItem) {
      const reason = skippedItem?._reason || "Skipped";
      const uniqueKey = this.uniqueFieldForUpdate;
      const uniqueValue =
        uniqueKey && skippedItem && skippedItem[uniqueKey] !== undefined
          ? skippedItem[uniqueKey]
          : null;
      if (uniqueKey && uniqueValue !== null && uniqueValue !== undefined && uniqueValue !== "") {
        return `${uniqueKey}="${uniqueValue}" - ${reason}`;
      }
      return reason;
    },
    async getReferenceModelItems(modelId) {
      if (!modelId) return [];
      if (Array.isArray(this.refModelItemsCache[modelId])) {
        return this.refModelItemsCache[modelId];
      }
      if (this.refModelItemsPromises[modelId]) {
        return this.refModelItemsPromises[modelId];
      }

      const inFlight = (async () => {
        try {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${modelId}?all=true`,
            this.getRequestConfig()
          );
          const items = Array.isArray(data) ? data : [];
          this.$set(this.refModelItemsCache, modelId, items);
          return items;
        } finally {
          this.$delete(this.refModelItemsPromises, modelId);
        }
      })();

      this.$set(this.refModelItemsPromises, modelId, inFlight);
      return inFlight;
    },
    normalizeText(text) {
      if (!text) return "";
      return String(text)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
        .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters but keep spaces and letters (case insensitive)
        .trim();
    },
    autoMapColumns() {
      // Auto-mapear columnas basándose en coincidencias de key o label
      this.previewHeaders.forEach((header) => {
        const trimmedHeader = header.trim().toLowerCase();
        
        // Buscar coincidencia exacta primero
        let matched = false;
        
        // Buscar en campos simples
        for (const field of this.modelFields) {
          if (field.type === "group") {
            // Para grupos, buscar subcampos
            for (const subField of field.fields) {
              const fieldKey = subField.key.trim().toLowerCase();
              const fieldLabel = subField.label.trim().toLowerCase();
              // Buscar formato grupo_campo
              const possibleFormats = [
                `${field.label}_${subField.label}`,
                `${field.key}_${subField.key}`,
                `${field.label}_${subField.key}`,
                `${field.key}_${subField.label}`,
              ];
              if (
                possibleFormats.some(
                  (format) => format.trim().toLowerCase() === trimmedHeader
                )
              ) {
                this.$set(
                  this.columnMapping,
                  header,
                  `${field.key}.${subField.key}`
                );
                matched = true;
                break;
              }
            }
            if (matched) break;
          } else {
            const fieldKey = field.key.trim().toLowerCase();
            const fieldLabel = field.label.trim().toLowerCase();
            if (
              trimmedHeader === fieldKey ||
              trimmedHeader === fieldLabel
            ) {
              this.$set(this.columnMapping, header, field.key);
              matched = true;
              break;
            }
          }
        }
        
        // Si no hay coincidencia, verificar si es "name" o "idModel"
        if (!matched && trimmedHeader === "name") {
          this.$set(this.columnMapping, header, "name");
        } else if (!matched && (trimmedHeader === "idmodel" || trimmedHeader === "id_model" || trimmedHeader === "id")) {
          this.$set(this.columnMapping, header, "idModel");
        }
      });
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.previewData = [];
      this.previewHeaders = [];

      Papa.parse(file, {
        header: true,
        preview: 5, // Mostrar solo las primeras 5 filas en la vista previa
        delimiter: this.selectedDelimiter,
        transformHeader: (header) => header.trim(), // Hacer trim de los nombres de las columnas
        complete: (results) => {
          this.previewHeaders = results.meta.fields;
          // Inicializar mapeo de columnas vacío
          this.columnMapping = {};
          this.previewHeaders.forEach((header) => {
            this.columnMapping[header] = "";
          });
          // Auto-mapear columnas
          this.autoMapColumns();
          // Procesar los datos para mostrar nombres en lugar de IDs para campos tipo list
          this.previewData = results.data.map((row) => {
            const processedRow = { ...row };
            this.modelFields.forEach((field) => {
              if (field.type === "list") {
                const value = row[field.label];
                if (value) {
                  if (field.isArray) {
                    // Para listas múltiples, procesar cada valor
                    let values = [];
                    // Dividir por comas, punto y coma o pipe
                    if (value && (value.includes(",") || value.includes(";") || value.includes("|"))) {
                      // Dividir por comas, punto y coma o pipe
                      const separators = /[,;|]/;
                      values = value.split(separators).map((v) => {
                        const trimmedValue = v.trim();
                        const list = Array.isArray(this.lists) ? this.lists.find(
                          (l) => l.type === field.listType
                        ) : null;
                        if (list && list.values) {
                          const listItem = list.values.find((item) => {
                            const itemId = String(item._id);
                            const itemLabel = String(item.label || "");
                            const searchValue = String(trimmedValue);
                            return (
                              itemId === searchValue ||
                              itemLabel === searchValue
                            );
                          });
                          if (listItem) {
                            return listItem.label;
                          }
                        }
                        return trimmedValue;
                      });
                    } else {
                      // Si no contiene comas, usar el delimitador del CSV
                      values = value.split(this.selectedDelimiter).map((v) => {
                        const trimmedValue = v.trim();
                        const list = Array.isArray(this.lists) ? this.lists.find(
                          (l) => l.type === field.listType
                        ) : null;
                        if (list && list.values) {
                          const listItem = list.values.find((item) => {
                            const itemId = String(item._id);
                            const itemLabel = String(item.label || "");
                            const searchValue = String(trimmedValue);
                            return (
                              itemId === searchValue ||
                              itemLabel === searchValue
                            );
                          });
                          if (listItem) {
                            return listItem.label;
                          }
                        }
                        return trimmedValue;
                      });
                    }
                    processedRow[field.label] = values.join(", ");
                  } else {
                    // Para listas simples
                    const list = Array.isArray(this.lists) ? this.lists.find(
                      (l) => l.type === field.listType
                    ) : null;
                    if (list && list.values) {
                      const listItem = list.values.find((item) => {
                        const itemId = String(item._id);
                        const itemLabel = String(item.label || "");
                        const searchValue = String(value);
                        return (
                          itemId === searchValue || itemLabel === searchValue
                        );
                      });
                      if (listItem) {
                        processedRow[field.label] = listItem.label;
                      }
                    }
                  }
                }
              }
            });
            return processedRow;
          });
        },
      });
    },
    async importData() {
      if (!this.selectedFile) return;

      this.isImporting = true;
      this.cancelRequested = false;
      this.requestAbortController = new AbortController();
      this.importSummary = null;
      this.importProgress = 0;
      this.currentRow = 0;
      this.processedRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.skippedCount = 0;
      this.skippedCount = 0;

      try {
        this.throwIfImportCancelled();
        // Primero procesamos el archivo CSV
        const csvData = await this.processCSVFile(this.selectedFile);
        this.totalRows = csvData.length;

        // Convertimos los datos según la definición del modelo
        const processedData = await Promise.all(
          csvData.map(async (row, index) => {
            this.throwIfImportCancelled();
            this.currentRow = index + 1;
            this.importProgress = Math.round((index / this.totalRows) * 50); // 50% para el procesamiento
            const processedRow = await this.processRowData(row);
            return processedRow;
          })
        );

        // Procesar cada fila individualmente
        const results = [];
        const errors = [];
        let cancelled = false;

        for (let i = 0; i < processedData.length; i++) {
          if (this.cancelRequested) {
            cancelled = true;
            break;
          }
          const row = processedData[i];
          this.currentRow = i + 1;
          this.processedRows = i + 1;
          this.importProgress =
            50 + Math.round((i / processedData.length) * 50); // 50% para el envío

          try {
            this.throwIfImportCancelled();
            // Procesar los datos antes de enviarlos
            const dataToSend = { ...row };

            // Función para verificar si un valor está vacío
            const isEmpty = (value) => {
              if (value === null || value === undefined) return true;
              if (value === "") return true;
              if (Array.isArray(value) && value.length === 0) return true;
              if (typeof value === "object" && Object.keys(value).length === 0)
                return true;
              return false;
            };

            // Función para limpiar un objeto manteniendo solo los campos definidos y no vacíos
            const cleanObject = (obj, fields) => {
              const cleaned = {};
              fields.forEach((field) => {
                const value = obj[field.key];
                // Solo incluir el campo si tiene un valor no vacío
                if (!isEmpty(value)) {
                  cleaned[field.key] = value;
                }
              });
              return cleaned;
            };

            // Procesar los campos según su tipo
            const processFields = (data, fields) => {
              Object.keys(data).forEach((key) => {
                // Encontrar la definición del campo
                const fieldDef = fields.find((f) => f.key === key);

                if (fieldDef) {
                  // Si es un campo de tipo reference, solo mantener el _id si no está vacío
                  if (
                    fieldDef.type === "reference" &&
                    data[key] &&
                    typeof data[key] === "object" &&
                    data[key]._id
                  ) {
                    data[key] = data[key]._id;
                  }
                  // Si es un campo de tipo group
                  else if (
                    fieldDef.type === "group" &&
                    data[key] &&
                    typeof data[key] === "object"
                  ) {
                    // Si es un array de grupos (isArray: true)
                    if (fieldDef.isArray && Array.isArray(data[key])) {
                      data[key] = data[key]
                        .map((item) => {
                          if (item && typeof item === "object") {
                            // Limpiar cada grupo del array
                            const cleanedItem = cleanObject(
                              item,
                              fieldDef.fields
                            );
                            // Solo mantener items que tengan al menos un campo no vacío
                            if (Object.keys(cleanedItem).length > 0) {
                              // Procesar los campos anidados de cada grupo
                              return processFields(
                                cleanedItem,
                                fieldDef.fields
                              );
                            }
                          }
                          return null;
                        })
                        .filter((item) => item !== null); // Eliminar items nulos
                    } else {
                      // Si es un grupo simple (no array)
                      data[key] = cleanObject(data[key], fieldDef.fields);
                      // Solo procesar si hay campos no vacíos
                      if (Object.keys(data[key]).length > 0) {
                        processFields(data[key], fieldDef.fields);
                      }
                    }
                  }
                  // Si es un array de otros tipos
                  else if (Array.isArray(data[key])) {
                    data[key] = data[key]
                      .map((item) => {
                        if (item && typeof item === "object") {
                          if (fieldDef.type === "reference" && item._id) {
                            return item._id;
                          }
                        }
                        return item;
                      })
                      .filter((item) => !isEmpty(item)); // Eliminar items vacíos
                  }
                }
              });
              return data;
            };

            // Procesar los campos en el nivel raíz
            processFields(dataToSend, this.modelFields);

            // Solo enviar si hay datos
            if (Object.keys(dataToSend).length > 0) {
              // Si hay un campo único configurado, buscar duplicados
              if (this.uniqueFieldForUpdate) {
                const uniqueValue = dataToSend[this.uniqueFieldForUpdate];
                
                // Si el campo único está vacío, null, undefined o es string vacío, crear nuevo
                const isEmptyUniqueValue = 
                  uniqueValue === null || 
                  uniqueValue === undefined || 
                  uniqueValue === "" ||
                  (typeof uniqueValue === "string" && uniqueValue.trim() === "") ||
                  (typeof uniqueValue === "number" && isNaN(uniqueValue));

                  if (isEmptyUniqueValue) {
                    // Campo único vacío
                    if (this.importMode === "update_only") {
                      // Modo "solo actualizar": omitir este item (no se puede buscar sin campo único)
                      console.log(`Unique field "${this.uniqueFieldForUpdate}" is empty, skipping (update_only mode)`);
                      results.push({ 
                        _id: null,
                        _action: "skipped",
                        _reason: "Unique field is empty (update_only mode)"
                      });
                      this.skippedCount++;
                    } else {
                      // Crear nuevo documento
                      console.log(`Unique field "${this.uniqueFieldForUpdate}" is empty, creating new item`);
                      // Agregar skipEmailTriggers: true para evitar enviar miles de emails durante la importación
                      const createData = {
                        ...dataToSend,
                        skipEmailTriggers: true
                      };
                      const response = await wencesApi.post(
                        `/api/gotrading/dynamic/${this.modelId}`,
                        createData,
                        this.getRequestConfig()
                      );
                      results.push({ ...response.data, _action: "created" });
                      this.successCount++;
                    }
                  } else {
                  // Campo único tiene valor: buscar si existe
                  try {
                    // Buscar documento existente usando filtro directo por campo único
                    const queryParams = new URLSearchParams({
                      page: "1",
                      perPage: "10",
                      [this.uniqueFieldForUpdate]: String(uniqueValue),
                    });
                    const searchResponse = await wencesApi.get(
                      `/api/gotrading/dynamic/${this.modelId}?${queryParams.toString()}`,
                      this.getRequestConfig()
                    );

                    const candidateItems = Array.isArray(searchResponse.data)
                      ? searchResponse.data
                      : Array.isArray(searchResponse.data?.items)
                      ? searchResponse.data.items
                      : [];

                    // Verificación exacta en front para evitar falsos positivos por búsquedas flexibles del backend
                    let existingItem;
                    if (this.uniqueFieldForUpdate === "idModel") {
                      const searchIdModel = Number(uniqueValue);
                      if (!isNaN(searchIdModel)) {
                        existingItem = candidateItems.find((item) => {
                          return (
                            item.idModel !== undefined &&
                            item.idModel !== null &&
                            Number(item.idModel) === searchIdModel
                          );
                        });
                      }
                    } else {
                      const normalizedSearchValue = this.normalizeText(
                        String(uniqueValue)
                      );
                      existingItem = candidateItems.find((item) => {
                        const itemValue = item[this.uniqueFieldForUpdate];
                        if (itemValue === undefined || itemValue === null)
                          return false;
                        return (
                          this.normalizeText(String(itemValue)) ===
                          normalizedSearchValue
                        );
                      });
                    }

                    if (existingItem) {
                      // Documento existente encontrado: actualizar
                      console.log(`Found existing item with ${this.uniqueFieldForUpdate}="${uniqueValue}", updating`);
                      // Agregar partialUpdate: true para que solo actualice los campos especificados sin validar el registro completo
                      // Agregar skipEmailTriggers: true para evitar enviar miles de emails durante la importación
                      const updateData = {
                        ...dataToSend,
                        partialUpdate: true,
                        skipEmailTriggers: true
                      };
                      const updateResponse = await wencesApi.put(
                        `/api/gotrading/dynamic/${this.modelId}/${existingItem._id}`,
                        updateData,
                        this.getRequestConfig()
                      );
                      results.push({ ...updateResponse.data, _action: "updated" });
                      this.successCount++;
                    } else {
                      // Campo único tiene valor pero no existe en el sistema
                      if (this.importMode === "update_only") {
                        // Modo "solo actualizar": omitir este item
                        console.log(`Unique field "${this.uniqueFieldForUpdate}"="${uniqueValue}" not found in system, skipping (update_only mode)`);
                        results.push({ 
                          _id: null, 
                          [this.uniqueFieldForUpdate]: uniqueValue,
                          _action: "skipped",
                          _reason: "Item does not exist (update_only mode)"
                        });
                        this.skippedCount++;
                      } else {
                        // Modo "actualizar y crear": crear nuevo item
                        console.log(`Unique field "${this.uniqueFieldForUpdate}"="${uniqueValue}" not found in system, creating new item`);
                        // Agregar skipEmailTriggers: true para evitar enviar miles de emails durante la importación
                        const createData = {
                          ...dataToSend,
                          skipEmailTriggers: true
                        };
                        const response = await wencesApi.post(
                          `/api/gotrading/dynamic/${this.modelId}`,
                          createData,
                          this.getRequestConfig()
                        );
                        results.push({ ...response.data, _action: "created" });
                        this.successCount++;
                      }
                    }
                  } catch (searchError) {
                    console.error("Error searching for duplicates:", searchError);
                    // Si falla la búsqueda y estamos en modo "solo actualizar", omitir
                    if (this.importMode === "update_only") {
                      console.log(`Error searching, skipping item (update_only mode)`);
                      results.push({ 
                        _id: null,
                        _action: "skipped",
                        _reason: "Error searching for existing item (update_only mode)"
                      });
                      this.skippedCount++;
                    } else {
                      // Si falla la búsqueda, crear nuevo documento
                      const response = await wencesApi.post(
                        `/api/gotrading/dynamic/${this.modelId}`,
                        dataToSend,
                        this.getRequestConfig()
                      );
                      results.push({ ...response.data, _action: "created" });
                      this.successCount++;
                    }
                  }
                }
              } else {
                // Sin campo único configurado
                if (this.importMode === "update_only") {
                  // En modo "solo actualizar", sin campo único no podemos buscar, así que omitir
                  console.log(`No unique field configured, skipping item (update_only mode)`);
                  results.push({ 
                    _id: null,
                    _action: "skipped",
                    _reason: "No unique field configured (update_only mode requires a unique field)"
                  });
                  this.skippedCount++;
                } else {
                  // Crear nuevo documento
                  const response = await wencesApi.post(
                    `/api/gotrading/dynamic/${this.modelId}`,
                    dataToSend,
                    this.getRequestConfig()
                  );
                  results.push({ ...response.data, _action: "created" });
                  this.successCount++;
                }
              }
            } else {
              throw new Error(
                "No hay datos para enviar después del procesamiento"
              );
            }
          } catch (error) {
            if (this.isRequestCancelled(error) || this.cancelRequested) {
              cancelled = true;
              break;
            }
            console.error("Error procesando fila:", error);
            errors.push({
              row,
              error: error.message,
            });
            this.errorCount++;
          }
        }

        if (cancelled || this.cancelRequested) {
          return;
        }

        this.importSummary = {
          success: this.successCount,
          skipped: this.skippedCount,
          errors: this.errorCount,
          skippedItems: results.filter((r) => r && r._action === "skipped"),
        };
        this.$emit("import-success", { results, errors });
      } catch (error) {
        if (this.isRequestCancelled(error) || this.cancelRequested) {
          return;
        }
        console.error("Error:", error);
        alert("Error al importar los datos: " + error.message);
      } finally {
        this.isImporting = false;
        this.requestAbortController = null;
        this.importProgress = 0;
        this.currentRow = 0;
        this.processedRows = 0;
        this.successCount = 0;
        this.errorCount = 0;
        this.skippedCount = 0;
      }
    },
    processCSVFile(file) {
      return new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          delimiter: this.selectedDelimiter,
          transformHeader: (header) => header.trim(), // Hacer trim de los nombres de las columnas
          complete: (results) => {
            resolve(results.data);
          },
          error: (error) => {
            reject(error);
          },
        });
      });
    },
    async processRowData(row) {
      const processedRow = {};

      // Usar mapeo manual si está disponible, sino usar mapeo automático
      const useManualMapping = Object.keys(this.columnMapping).length > 0;

      if (useManualMapping) {
        // Procesar usando mapeo manual
        for (const [csvColumn, mappedField] of Object.entries(
          this.columnMapping
        )) {
          if (!mappedField || mappedField === "") continue;

          const value = row[csvColumn];
          if (value === undefined || value === "") continue;

          // Si el campo mapeado es "name"
          if (mappedField === "name") {
            processedRow.name = value;
            continue;
          }

          // Si el campo mapeado es "idModel"
          if (mappedField === "idModel") {
            // Convertir a número si es posible
            const numValue = Number(value);
            processedRow.idModel = !isNaN(numValue) ? numValue : value;
            continue;
          }

          // Si el campo mapeado es un campo de grupo (formato: grupo.campo)
          if (mappedField.includes(".")) {
            const [groupKey, subFieldKey] = mappedField.split(".");
            const groupField = this.modelFields.find(
              (f) => f.key === groupKey && f.type === "group"
            );

            if (groupField) {
              const subField = groupField.fields.find(
                (f) => f.key === subFieldKey
              );

              if (subField) {
                if (!processedRow[groupKey]) {
                  processedRow[groupKey] = {};
                }
                processedRow[groupKey][subFieldKey] =
                  await this.processFieldValue(subField, value);
              }
            }
          } else {
            console.log(`[COLUMN MAPPING] Procesando campo simple: "${mappedField}" con valor: "${value}"`);
            const field = this.modelFields.find((f) => f.key === mappedField);
            console.log(`[COLUMN MAPPING] Campo encontrado:`, field ? { key: field.key, type: field.type, label: field.label } : 'NO ENCONTRADO');
            if (field && field.type === "group") {
              const parsed = await this.processWholeGroupFieldFromCsvValue(
                field,
                value
              );
              if (
                parsed !== undefined &&
                parsed !== null &&
                !(Array.isArray(parsed) && parsed.length === 0) &&
                !(
                  typeof parsed === "object" &&
                  !Array.isArray(parsed) &&
                  Object.keys(parsed).length === 0
                )
              ) {
                processedRow[mappedField] = parsed;
              }
            } else if (field && field.type !== "group") {
              console.log(`[COLUMN MAPPING] Llamando processFieldValue para "${mappedField}" (tipo: ${field.type})`);
              const processedValue = await this.processFieldValue(
                field,
                value
              );
              console.log(`[COLUMN MAPPING] Valor procesado para "${mappedField}":`, processedValue);
              processedRow[mappedField] = processedValue;
            } else if (!field) {
              console.warn(`[COLUMN MAPPING] ⚠️ Campo "${mappedField}" no encontrado en modelFields`);
            }
          }
        }
      } else {
        // Mapeo automático (código original)
        // Procesar el campo name si existe en el CSV
        const nameColumn = Object.keys(row).find((col) => col.trim() === "name");
        if (nameColumn) {
          processedRow.name = row[nameColumn];
        }

        for (const field of this.modelFields) {
          if (field.type === "group") {
            // Procesar campos de grupo
            const groupData = {};
            for (const subField of field.fields) {
              // Buscar la columna que coincida con el formato grupo_campo
              const subFieldColumn = Object.keys(row).find((col) => {
                const trimmedCol = col;
                const possibleFormats = [
                  `${field.label}_${subField.label}`,
                  `${field.key}_${subField.key}`,
                  `${field.label}_${subField.key}`,
                  `${field.key}_${subField.label}`,
                ];
                return possibleFormats.includes(trimmedCol);
              });

              if (subFieldColumn) {
                const value = row[subFieldColumn];
                if (value !== undefined && value !== "") {
                  groupData[subField.key] = await this.processFieldValue(
                    subField,
                    value
                  );
                }
              }
            }

            // Solo agregar el grupo si tiene al menos un campo con valor
            if (Object.keys(groupData).length > 0) {
              processedRow[field.key] = groupData;
            }
          } else {
            // Procesar campos simples
            const csvColumn = Object.keys(row).find((col) => {
              const trimmedCol = col.trim();
              const fieldKey = field.key.trim();
              const fieldLabel = field.label.trim();
              return trimmedCol === fieldKey || trimmedCol === fieldLabel;
            });

            if (csvColumn) {
              const value = row[csvColumn];
              if (value !== undefined && value !== "") {
                processedRow[field.key] = await this.processFieldValue(
                  field,
                  value
                );
              }
            }
          }
        }
      }

      return processedRow;
    },
    async loadAllReferenceOptions() {
      for (const field of this.modelFields) {
        if (field.type === "reference" && field.ref) {
          try {
            const { data } = await wencesApi.get(
              `/api/gotrading/dynamic/${field.ref}?all=true`
            );
            this.$set(this.referenceOptions, field.key, data);
          } catch (error) {
            console.error(
              `Error loading reference options for ${field.key}:`,
              error
            );
            this.$set(this.referenceOptions, field.key, []);
          }
        }
      }
    },
    loadListOptions() {
      console.log("Loading list options...");
      if (!this.lists || !Array.isArray(this.lists)) {
        console.error("Lists prop is not valid:", this.lists);
        return;
      }

      this.modelFields.forEach((field) => {
        if (field.type === "list" && field.listType) {
          console.log("Processing list field:", {
            fieldKey: field.key,
            listType: field.listType,
            availableLists: this.lists.map((l) => l.type),
          });

          const list = Array.isArray(this.lists) ? this.lists.find((l) => l.type === field.listType) : null;
          console.log("Found list:", list);

          if (list && list.values) {
            this.$set(this.listOptions, field.key, list.values);
          } else {
            console.warn(`List not found for type: ${field.listType}`);
          }
        }
      });
    },
    parseDate(value) {
      if (!value) return null;

      // Intentar diferentes formatos de fecha
      const formats = [
        // Formato ISO (2024-03-12T15:30:00)
        (val) => {
          const date = new Date(val);
          return !isNaN(date.getTime()) ? date : null;
        },
        // Formato DD-MM-YYYY o DD/MM/YYYY
        (val) => {
          const parts = val.split(/[-\/]/);
          if (parts.length === 3) {
            const [day, month, year] = parts;
            const date = new Date(year, month - 1, day);
            return !isNaN(date.getTime()) ? date : null;
          }
          return null;
        },
        // Formato MM-DD-YYYY o MM/DD/YYYY (estilo americano)
        (val) => {
          const parts = val.split(/[-\/]/);
          if (parts.length === 3) {
            const [month, day, year] = parts;
            const date = new Date(year, month - 1, day);
            return !isNaN(date.getTime()) ? date : null;
          }
          return null;
        },
        // Formato YYYY-MM-DD
        (val) => {
          const parts = val.split("-");
          if (parts.length === 3) {
            const [year, month, day] = parts;
            const date = new Date(year, month - 1, day);
            return !isNaN(date.getTime()) ? date : null;
          }
          return null;
        },
        // Formato DD-MM-YYYY HH:mm o DD/MM/YYYY HH:mm
        (val) => {
          const [datePart, timePart] = val.split(" ");
          if (datePart && timePart) {
            const parts = datePart.split(/[-\/]/);
            if (parts.length === 3) {
              const [day, month, year] = parts;
              const [hours, minutes] = timePart.split(":");
              const date = new Date(year, month - 1, day, hours, minutes);
              return !isNaN(date.getTime()) ? date : null;
            }
          }
          return null;
        },
        // Formato MM-DD-YYYY HH:mm o MM/DD/YYYY HH:mm (estilo americano)
        (val) => {
          const [datePart, timePart] = val.split(" ");
          if (datePart && timePart) {
            const parts = datePart.split(/[-\/]/);
            if (parts.length === 3) {
              const [month, day, year] = parts;
              const [hours, minutes] = timePart.split(":");
              const date = new Date(year, month - 1, day, hours, minutes);
              return !isNaN(date.getTime()) ? date : null;
            }
          }
          return null;
        },
        // Formato YYYY-MM-DD HH:mm
        (val) => {
          const [datePart, timePart] = val.split(" ");
          if (datePart && timePart) {
            const parts = datePart.split("-");
            if (parts.length === 3) {
              const [year, month, day] = parts;
              const [hours, minutes] = timePart.split(":");
              const date = new Date(year, month - 1, day, hours, minutes);
              return !isNaN(date.getTime()) ? date : null;
            }
          }
          return null;
        },
      ];

      // Intentar cada formato hasta encontrar uno que funcione
      for (const format of formats) {
        const date = format(value);
        if (date) {
          return date.toISOString();
        }
      }

      // Si ningún formato funciona, intentar el constructor Date directamente
      const date = new Date(value);
      return !isNaN(date.getTime()) ? date.toISOString() : null;
    },

    async processWholeGroupFieldFromCsvValue(field, rawValue) {
      let data = rawValue;
      if (typeof data === "string") {
        const t = data.trim();
        if (!t) {
          return field.isArray ? [] : {};
        }
        try {
          data = JSON.parse(t);
        } catch (e) {
          throw new Error(
            `El campo «${field.label}» (${field.key}) espera JSON válido: ${e.message}`
          );
        }
      }
      if (field.isArray) {
        if (!Array.isArray(data)) {
          data =
            data && typeof data === "object" ? [data] : [];
        }
        const processedGroups = [];
        for (const groupItem of data) {
          if (groupItem && typeof groupItem === "object") {
            const processedGroup = {};
            for (const subField of field.fields || []) {
              const sv =
                groupItem[subField.key] !== undefined &&
                groupItem[subField.key] !== null
                  ? groupItem[subField.key]
                  : groupItem[subField.label];
              if (sv !== undefined && sv !== "") {
                processedGroup[subField.key] = await this.processFieldValue(
                  subField,
                  sv
                );
              }
            }
            if (Object.keys(processedGroup).length > 0) {
              processedGroups.push(processedGroup);
            }
          }
        }
        return processedGroups;
      }
      if (Array.isArray(data) && data.length > 0) {
        data = data[0];
      }
      if (!data || typeof data !== "object") {
        return {};
      }
      const processedGroup = {};
      for (const subField of field.fields || []) {
        const sv =
          data[subField.key] !== undefined && data[subField.key] !== null
            ? data[subField.key]
            : data[subField.label];
        if (sv !== undefined && sv !== "") {
          processedGroup[subField.key] = await this.processFieldValue(
            subField,
            sv
          );
        }
      }
      return processedGroup;
    },

    async processFieldValue(field, value) {
      console.log(`[PROCESS FIELD VALUE] Iniciando procesamiento:`, {
        fieldKey: field.key,
        fieldType: field.type,
        fieldLabel: field.label,
        value: value,
        valueType: typeof value
      });
      
      if (value === "") {
        console.log(`[PROCESS FIELD VALUE] Valor vacío, retornando null`);
        return null;
      }

      switch (field.type) {
        case "string":
          return value;
        case "number":
          return Number(value);
        case "date":
          return this.parseDate(value);
        case "boolean":
          return value === "true";
        case "reference": {
          const raw = value == null ? "" : String(value).trim();
          if (!raw) return null;

          const searchByNidThenName = (items) => {
            if (!Array.isArray(items) || !items.length) return null;
            const rawNum = Number.parseInt(raw, 10);
            const byNid = items.find((ref) => {
              if (!ref || ref.nid === undefined || ref.nid === null) return false;
              const refNidStr = String(ref.nid).trim();
              const refNidNum = Number.parseInt(refNidStr, 10);
              return (
                refNidStr === raw ||
                (!Number.isNaN(rawNum) && !Number.isNaN(refNidNum) && refNidNum === rawNum)
              );
            });
            if (byNid) return byNid;

            const searchNorm = this.normalizeText(raw).trim().toLowerCase();
            if (!searchNorm) return null;
            const exact = items.find((ref) => {
              const refName = this.normalizeText(ref?.name || "").trim().toLowerCase();
              const refLabel = this.normalizeText(ref?.label || "").trim().toLowerCase();
              return refName === searchNorm || refLabel === searchNorm;
            });
            if (exact) return exact;
            return (
              items.find((ref) => {
                const refName = this.normalizeText(ref?.name || "").trim().toLowerCase();
                const refLabel = this.normalizeText(ref?.label || "").trim().toLowerCase();
                return refName.includes(searchNorm) || refLabel.includes(searchNorm);
              }) || null
            );
          };

          const localMatch = searchByNidThenName(this.referenceOptions[field.key]);
          if (localMatch && localMatch._id) return localMatch._id;

          if (field.ref) {
            try {
              const remoteItems = await this.getReferenceModelItems(field.ref);
              const remoteMatch = searchByNidThenName(remoteItems);
              if (remoteMatch && remoteMatch._id) return remoteMatch._id;
            } catch (error) {
              console.error(
                `Error searching reference ${raw} in ${field.ref}:`,
                error
              );
            }
          }
          return null;
        }
        case "list":
          // El backend procesará las jerarquías automáticamente
          // Solo devolvemos el string tal cual viene del CSV
          // El backend detectará si tiene formato "A > B" y lo procesará
          if (!value || (typeof value === 'string' && !value.trim())) {
            return null;
          }
          // Para arrays, dividir y devolver cada valor como string
          if (field.isArray) {
            let valuesToProcess = [];
            if (value && (value.includes(",") || value.includes(";") || value.includes("|"))) {
              const separators = /[,;|]/;
              valuesToProcess = value
                .split(separators)
                .map((v) => v.trim())
                .filter((v) => v);
            } else {
              valuesToProcess = value
                .split(this.selectedDelimiter)
                .map((v) => v.trim())
                .filter((v) => v);
            }
            return valuesToProcess;
          } else {
            // Devolver el string tal cual - el backend lo procesará
            return typeof value === 'string' ? value.trim() : String(value);
          }
        case "image":
          return value;
        default:
          return value;
      }
    },
    downloadSampleCSV() {
      // Crear headers basados en los campos del modelo
      const headers = [
        "name",
        ...this.modelFields
          .map((field) => {
            if (field.type === "group" && field.isArray) {
              // Para campos de grupo tipo array, agregar cada subcampo
              return field.fields.map(
                (subField) => `${field.label}_${subField.label}`
              );
            } else if (field.type === "group") {
              // Para campos de grupo simples, agregar cada subcampo
              return field.fields.map(
                (subField) => `${field.label}_${subField.label}`
              );
            } else {
              return field.label;
            }
          })
          .flat(),
      ];

      // Crear una fila de ejemplo con valores de muestra
      const sampleRow = {
        name: "Ejemplo de nombre",
        ...this.modelFields.reduce((row, field) => {
          if (field.type === "group" && field.isArray) {
            // Para campos de grupo tipo array, agregar valores de ejemplo para cada subcampo
            field.fields.forEach((subField) => {
              row[`${field.label}_${subField.label}`] =
                this.getSampleValue(subField);
            });
          } else if (field.type === "group") {
            // Para campos de grupo simples, agregar valores de ejemplo para cada subcampo
            field.fields.forEach((subField) => {
              row[`${field.label}_${subField.label}`] =
                this.getSampleValue(subField);
            });
          } else {
            row[field.label] = this.getSampleValue(field);
          }
          return row;
        }, {}),
      };

      // Convertir a CSV usando el separador seleccionado
      const csv = Papa.unparse(
        {
          fields: headers,
          data: [sampleRow],
        },
        {
          delimiter: this.selectedDelimiter,
        }
      );

      // Crear y descargar el archivo
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${this.modelName}_ejemplo.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    getSampleValue(field) {
      switch (field.type) {
        case "string":
          return "Ejemplo de texto";
        case "number":
          return "123";
        case "date":
          // Mostrar varios formatos de ejemplo
          return "12/03/2024 o 2024-03-12 o 12-03-2024 15:30";
        case "boolean":
          return "true";
        case "reference":
          if (this.referenceOptions[field.key]?.length > 0) {
            const firstRef = this.referenceOptions[field.key][0];
            return firstRef.name || firstRef.label || firstRef._id;
          }
          return "ID_REFERENCIA";
        case "list":
          if (this.listOptions[field.key]?.length > 0) {
            if (field.isArray) {
              const firstTwo = this.listOptions[field.key].slice(0, 2);
              return firstTwo
                .map((item) => item.label || item._id)
                .join(this.selectedDelimiter);
            } else {
              const firstItem = this.listOptions[field.key][0];
              return firstItem.label || firstItem._id;
            }
          }
          return field.isArray ? "valor1,valor2" : "valor1";
        case "image":
          return "URL_IMAGEN";
        default:
          return "valor_ejemplo";
      }
    },
    async loadLists() {
      try {
        const response = await wencesApi.get("/api/gotrading/lists");
        this.lists = response.data;
        console.log(
          "Loaded lists:",
          this.lists.map((list) => ({
            type: list.type,
            values: list.values.map((v) => ({ id: v._id, label: v.label })),
          }))
        );
      } catch (error) {
        console.error("Error loading lists:", error);
      }
    },
  },
};
</script>

<style scoped>
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.table-sm td,
.table-sm th {
  padding: 0.5rem;
}
</style>
